import apiClient from './client'
import { Bot, CustomBlockTemplate, CustomBlockTemplateRequest, ListBotAccessResponse, Resource } from '../types'

export const botsApi = {
  // List bots
  listBots: async (ownerId: string): Promise<Bot[]> => {
    console.log('[BotsAPI] Fetching bots for owner:', ownerId)
    console.log('[BotsAPI] Base URL:', import.meta.env.VITE_OPSERVICE_BASE_URL)

    try {
      const response = await apiClient.get(`/api/v1/bots?owner_id=${ownerId}`)
      console.log('[BotsAPI] Response:', response.data)
      return response.data.bots || []
    } catch (error) {
      console.error('[BotsAPI] Error fetching bots:', error)
      throw error
    }
  },

  // List bots with access
  listBotsWithAccess: async (): Promise<Bot[]> => {
    console.log('[BotsAPI] Fetching bots with access')
    try {
      const response = await apiClient.get('/api/v1/bots/accessible')
      console.log('[BotsAPI] Accessible bots response:', response.data)
      return response.data.bots || []
    } catch (error) {
      console.error('[BotsAPI] Error fetching accessible bots:', error)
      throw error
    }
  },

  // Get bot by ID
  getBot: async (botId: string): Promise<Bot> => {
    const response = await apiClient.get(`/api/v1/bots/${botId}`)
    return response.data
  },

  // Create bot
  createBot: async (data: {
    owner_id: string
    bot_token: string
    name?: string
    description?: string
    category?: string
    pricing_config?: any
  }): Promise<Bot> => {
    const response = await apiClient.post('/api/v1/bots', data)
    return response.data
  },

  // Update bot
  updateBot: async (botId: string, data: Partial<Bot>): Promise<Bot> => {
    const response = await apiClient.put(`/api/v1/bots/${botId}`, data)
    return response.data
  },

  // Delete bot
  deleteBot: async (botId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/bots/${botId}`)
  },

  // Toggle bot enabled/disabled
  toggleBot: async (botId: string): Promise<Bot> => {
    const response = await apiClient.post(`/api/v1/bots/${botId}/toggle`)
    return response.data
  },

  // Pause bot
  pauseBot: async (botId: string): Promise<Bot> => {
    const response = await apiClient.post(`/api/v1/bots/${botId}/pause`)
    return response.data
  },

  // Resume bot
  resumeBot: async (botId: string): Promise<Bot> => {
    const response = await apiClient.post(`/api/v1/bots/${botId}/resume`)
    return response.data
  },
  // Update bot integration
  updateBotIntegration: async (botId: string, data: { flyer_key?: string | null; subgram_key?: string | null; tgrass_key?: string | null; gramads_key?: string | null }): Promise<Bot> => {
    const response = await apiClient.put(`/api/v1/bots/${botId}`, data)
    return response.data
  },

  // Regenerate bot API key
  regenerateBotKey: async (botId: string): Promise<Bot> => {
    const response = await apiClient.put(`/api/v1/bots/${botId}/regenerate_key`)
    return response.data
  },

  // Get custom block template
  getCustomBlockTemplate: async (botId: string): Promise<CustomBlockTemplate> => {
    const response = await apiClient.get(`/api/v1/bots/${botId}/custom_block_template`)
    return response.data
  },

  // Create custom block template
  createCustomBlockTemplate: async (botId: string, data: CustomBlockTemplateRequest): Promise<CustomBlockTemplate> => {
    const response = await apiClient.post(`/api/v1/bots/${botId}/custom_block_template`, data)
    return response.data
  },

  // Update custom block template
  updateCustomBlockTemplate: async (botId: string, data: CustomBlockTemplateRequest): Promise<CustomBlockTemplate> => {
    const response = await apiClient.put(`/api/v1/bots/${botId}/custom_block_template`, data)
    return response.data
  },

  // Delete custom block template
  deleteCustomBlockTemplate: async (botId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/bots/${botId}/custom_block_template`)
  },

  // Bot access management
  getBotAccess: async (botId: string): Promise<ListBotAccessResponse> => {
    const response = await apiClient.get(`/api/v1/bots/${botId}/access`)
    return response.data
  },

  grantBotAccess: async (botId: string, telegramUserId: number, role: 'admin' | 'editor'): Promise<void> => {
    await apiClient.post(`/api/v1/bots/${botId}/access`, {
      telegram_user_id: telegramUserId,
      role,
    })
  },

  revokeBotAccess: async (botId: string, telegramUserId: number): Promise<void> => {
    await apiClient.delete(`/api/v1/bots/${botId}/access/${telegramUserId}`)
  },

  // Whitelist management
  getWhitelistedResources: async (botId: string): Promise<{ resource_ids: string[], resources: Resource[] }> => {
    const response = await apiClient.get(`/api/v1/bots/${botId}/whitelisted_resources`)
    return {
      resource_ids: response.data.resource_ids || [],
      resources: response.data.resources || [],
    }
  },

  setWhitelistedResources: async (botId: string, resourceIds: string[]): Promise<void> => {
    await apiClient.put(`/api/v1/bots/${botId}/whitelisted_resources`, {
      resource_ids: resourceIds,
    })
  },

  addWhitelistedResource: async (botId: string, resourceId: string): Promise<void> => {
    await apiClient.post(`/api/v1/bots/${botId}/whitelisted_resources/${resourceId}`)
  },

  removeWhitelistedResource: async (botId: string, resourceId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/bots/${botId}/whitelisted_resources/${resourceId}`)
  },

  // Auto-resource toggle
  toggleAutoResource: async (botId: string, autoResource: boolean): Promise<Bot> => {
    const response = await apiClient.put(`/api/v1/bots/${botId}`, {
      auto_resource: autoResource,
    })
    return response.data
  },
}
